import { jsPDF } from 'jspdf';
import { autoTable } from 'jspdf-autotable';
import xlsx from 'xlsx-js-style';
import { ShippingManifestDetail } from '@/types';
import { expandVinRange, formatReferenceVinRange } from './manifestEditor';
import { timeConverter } from '@/utils/helpers';

const VIN_EXPORT_LIMIT = 100000;

const referenceRows = (detail: ShippingManifestDetail) =>
  detail.shippingReferences.map((reference) => [
    reference.reference,
    String(reference.vehicleCount),
    formatReferenceVinRange(
      reference.startVin,
      reference.endVin,
      reference.vehicleCount,
    ),
  ]);

const totalVehicles = (detail: ShippingManifestDetail) =>
  detail.shippingReferences.reduce(
    (sum, reference) => sum + (reference.vehicleCount || 0),
    0,
  );

export const downloadManifestPdf = (detail: ShippingManifestDetail) => {
  const doc = new jsPDF();
  doc.setFontSize(14);
  doc.text(`Manifest ${detail.manifestRef}`, 14, 16);
  autoTable(doc, {
    startY: 24,
    theme: 'plain',
    body: [
      ['Manifest Ref', detail.manifestRef],
      ['Vessel', detail.vesselName || '—'],
      ['Voyage', detail.voyage || '—'],
      ['Customer', detail.customerName || '—'],
      ['Site / Terminal', detail.site || detail.dischargePort || '—'],
      ['Destination', detail.dischargePort || '—'],
      ['ETA', timeConverter(detail.eta) || '—'],
      ['Total Vehicles', String(totalVehicles(detail))],
    ],
  });
  autoTable(doc, {
    head: [['Shipping Reference', 'Vehicles', 'VIN Range']],
    body: referenceRows(detail),
  });
  doc.save(`Manifest ${detail.manifestRef}.pdf`);
};

const sheetName = (reference: string, used: Set<string>) => {
  const cleaned =
    reference
      .replace(/[\\/?*[\]:]/g, ' ')
      .trim()
      .slice(0, 31) || 'VIN';
  let name = cleaned;
  let suffix = 2;
  while (used.has(name)) {
    const ending = ` ${suffix}`;
    name = `${cleaned.slice(0, 31 - ending.length)}${ending}`;
    suffix += 1;
  }
  used.add(name);
  return name;
};

export const vehicleListSheets = (detail: ShippingManifestDetail) => {
  const used = new Set<string>();
  return detail.shippingReferences.map((reference) => ({
    name: sheetName(reference.reference, used),
    rows: (reference.vins?.length
      ? reference.vins
      : expandVinRange(
          reference.startVin,
          reference.vehicleCount,
          VIN_EXPORT_LIMIT,
        )
    ).map((vin) => [vin]),
  }));
};

export const downloadVehicleList = (detail: ShippingManifestDetail) => {
  const workbook = xlsx.utils.book_new();
  const sheets = vehicleListSheets(detail);
  const pages = sheets.length ? sheets : [{ name: 'VIN', rows: [] }];
  pages.forEach((page) => {
    const sheet = xlsx.utils.aoa_to_sheet(page.rows);
    xlsx.utils.book_append_sheet(workbook, sheet, page.name);
  });
  xlsx.writeFile(workbook, `Vehicle list ${detail.manifestRef}.xlsx`);
};
